import { useAuth } from '@/auth/auth-provider'
import { isStaffRole, isSuper, parsePrincipalFromUser } from '@/auth/principal'
import { useDealership } from '@/contexts/DealershipContext'
import { DealershipGroup } from '@/types'
import { GROUP_INTERVAL_LABELS } from '@/utils/groupDashboardInterval'
import { Badge, Button, Card, Group, Stack, Text, Title } from '@mantine/core'
import { IconBuilding, IconMail, IconMapPin, IconPhone, IconSettings } from '@tabler/icons-react'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

export function DealersList() {
  const { user } = useAuth()
  const { selectedEntity, setSelectedEntity, findEntityById, groupDashboardInterval } = useDealership()
  const navigate = useNavigate()
  const { isSuperUser, isStaff } = useMemo(() => {
    const principal = parsePrincipalFromUser(user)
    return { isSuperUser: isSuper(principal), isStaff: isStaffRole(principal) }
  }, [user])

  if (!selectedEntity || selectedEntity.type !== 'group') {
    return <Text>Please select a dealership group</Text>
  }

  /** Prefer live tree from context so stats refresh with group interval / React Query */
  const group =
    (findEntityById(selectedEntity.id, 'group') as DealershipGroup | null) ?? (selectedEntity as DealershipGroup)

  // Staff land on conversations; super admins, who configure dealerships, land on configuration.
  const handleViewDealership = (dealershipId: string, path = isStaff ? '/dealership/conversations' : '/dealership/configuration') => {
    const dealer = findEntityById(dealershipId, 'dealership')
    if (dealer) {
      setSelectedEntity(dealer)
      navigate(path)
    }
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <div>
          <Title order={2}>Dealerships</Title>
          <Text size="sm" c="dimmed">
            Manage and monitor all dealerships in {group.name}
          </Text>
          <Text size="xs" c="dimmed" mt={2}>
            Store metrics use report period: {GROUP_INTERVAL_LABELS[groupDashboardInterval]} (same as Group Overview & dealership dashboards)
          </Text>
        </div>
        {isSuperUser && (
          <Button variant="filled" leftSection={<IconBuilding size={18} />} onClick={() => navigate('/group/dealers/new')}>
            Add Dealership
          </Button>
        )}
      </Group>

      <Stack gap="md">
        {group.dealerships.map(dealership => (
          <Card
            key={dealership.id}
            shadow="sm"
            padding="lg"
            radius="md"
            withBorder
            style={{
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onClick={() => handleViewDealership(dealership.id)}
            onMouseEnter={e => {
              e.currentTarget.style.transform = 'translateY(-2px)'
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)'
            }}
            onMouseLeave={e => {
              e.currentTarget.style.transform = 'translateY(0)'
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0, 0, 0, 0.12)'
            }}
          >
            <Stack gap="md">
              <Group justify="space-between">
                <Group gap="md">
                  <IconBuilding size={32} stroke={1.5} />
                  <div>
                    <Text size="lg" fw={600}>
                      {dealership.name}
                    </Text>
                    <Text size="sm" c="dimmed">
                      ID: {dealership.id}
                    </Text>
                  </div>
                </Group>
                <Badge color={dealership.status === 'active' ? 'green' : 'gray'} variant="light" size="lg">
                  {dealership.status}
                </Badge>
              </Group>

              <Group gap="xl">
                {dealership.location && (
                  <Group gap="xs">
                    <IconMapPin size={16} stroke={1.5} />
                    <Text size="sm" c="dimmed">
                      {dealership.location}
                    </Text>
                  </Group>
                )}
                {dealership.phone && (
                  <Group gap="xs">
                    <IconPhone size={16} stroke={1.5} />
                    <Text size="sm" c="dimmed">
                      {dealership.phone}
                    </Text>
                  </Group>
                )}
                {dealership.email && (
                  <Group gap="xs">
                    <IconMail size={16} stroke={1.5} />
                    <Text size="sm" c="dimmed">
                      {dealership.email}
                    </Text>
                  </Group>
                )}
              </Group>

              <Group gap="xl" wrap="wrap">
                <div>
                  <Text size="xs" c="dimmed">
                    Conversations
                  </Text>
                  <Text size="xl" fw={600} c="blue">
                    {dealership.totalConversations ?? 0}
                  </Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">
                    Total appts
                  </Text>
                  <Text size="xl" fw={600} c="indigo">
                    {dealership.totalAppointments ?? 0}
                  </Text>
                </div>
                <div>
                  <Text size="xs" c="dimmed">
                    Upcoming appts
                  </Text>
                  <Text size="xl" fw={600} c="orange">
                    {dealership.upcomingAppointments ?? 0}
                  </Text>
                </div>
              </Group>

              <Group gap="sm">
                <Button
                  variant="filled"
                  size="sm"
                  leftSection={<IconSettings size={16} />}
                  onClick={e => {
                    e.stopPropagation()
                    handleViewDealership(dealership.id, '/dealership/configuration')
                  }}
                >
                  {isStaff ? 'View configuration' : 'Configure'}
                </Button>
              </Group>
            </Stack>
          </Card>
        ))}
      </Stack>
    </Stack>
  )
}
